import { RegionUserProfile } from "@/services/authService";
import { Team } from "@/services/teamService";
import { Guest } from "./guest";
import { Reservation } from "./reservation";
import { Room } from "./room";
import { Task } from "@/types/tasks";
import {
  BillingPaymentMethod,
  BillingPaymentStatus,
  BillingTransactionStatus,
} from "./billing-transactions";

export enum RequestType {
  GUEST = "GUEST",
  INTERNAL = "INTERNAL",
  ROOM = "ROOM",
}

export enum RequestStatus {
  NEW_TASK = "NEW_TASK",
  IN_PROGRESS = "IN_PROGRESS",
  ON_HOLD = "ON_HOLD",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
  TO_DO = "TO_DO",
  APPROVED = "APPROVED",
  DONE = "DONE",
}

export enum RoomUpgradeRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export type PaymentStatus = BillingPaymentStatus;

export interface User {
  name: string;
  avatar?: string;
}

export interface RequestServiceStaff {
  id: string;
  profile: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
  };
}

export interface RequestChecklistItem {
  id: string;
  serviceRequestId: string;
  title: string;
  isChecked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Service {
  id: string;
  systemKey?: string | null;
  name: string;
  image?: string;
  photoId?: string | null;
  description?: string | null;
  price?: string | number | null;
  menu?: string;
  discount?: number | null;
  hotelId?: string;
  amenityId?: string | null;
  categoryId?: string | null;
  status?: string;
  providerType?: string;
  isRecommended?: boolean;
  isAutomaticallyCreated?: boolean;
  autoCreateTriggers?: string[];
  checkinAvailable?: boolean;
  checkoutAvailable?: boolean;
  paymentEnabled?: boolean;
  paymentRequired?: boolean;
  availableFromTime?: string | null;
  availableToTime?: string | null;
  availableDays?: string[];
  staffId?: string | null;
  teamId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  regionUserProfileId?: string | null;
  staff?: RequestServiceStaff | null;
  team?: Team | null;
}

export interface BookingRoomUpgradeRequest {
  id: string;
  guestId: string;
  upgradeRoomId: string;
  bookingId: string;
  serviceRequestId: string | null;
  priceDifference: number;
  status: RoomUpgradeRequestStatus;
  createdAt: string;
  updatedAt: string;
  upgradeRoom?: Room | null;
}

export interface Request {
  id: string;
  description?: string | null;
  notes?: string | null;
  type: RequestType;
  priority: boolean;
  guest?: Guest | null;
  guestId?: string | null;
  room?: Room | null;
  bookingId?: string | null;
  booking?: Reservation | null;
  service?: Service | null;
  roomId?: string | null;
  serviceId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  dueDateTime: string;
  assignedStaff?: User[] | null;
  createdById?: string | null;
  createdByUserProfile?: RegionUserProfile | null;
  paymentStatus?: PaymentStatus | null;
  paymentMethod?: BillingPaymentMethod | null;
  assignedToStaffId?: string | string[] | null;
  status?: RequestStatus;
  hotelId?: string;
  checklistItems?: RequestChecklistItem[];
  bookingRoomUpgradeRequest?: BookingRoomUpgradeRequest | null;

  tasks?: Task[];
  quantity: number;
}

export interface RequestFilters {
  type?: RequestType;
  status?: string[];
  sortField?: string;
  sortDirection?: "asc" | "desc";
  page?: number;
  limit?: number;
  hotelId?: string;
  startDate?: string | null;
  endDate?: string | null;
  dueDateTime?: string;
  dueDateTimeStart?: string;
  dueDateTimeEnd?: string;
  servicesIds?: string[];
  createdByIds?: string[];
  assignedStaffIds?: string[];
  bookingId?: string | null;
  priority?: boolean;
  unassigned?: boolean;
  tenantId?: string;
  amenities?: string[];
}

export interface CreateRequest {
  type: RequestType;
  status?: RequestStatus;
  priority: boolean;
  guestId?: string;
  bookingId?: string;
  roomId?: string;
  serviceId?: string;
  dueDateTime: string;
  description?: string;
  notes?: string;
  quantity?: number;
  assignedToStaffId?: string;
  teamStaffId?: string;
  paymentStatus?: PaymentStatus;
  hotelId?: string;
  checklistItems?: string[];
}

export interface UpdateRequest {
  type?: RequestType;
  priority?: boolean;
  guestId?: string;
  roomId?: string;
  serviceId?: string;
  dueDate?: string;
  description?: string;
}

export interface OfflineServiceRequestPaymentResponse {
  transactionId: string;
  status: BillingTransactionStatus;
  paymentMethod: BillingPaymentMethod;
  amount: number;
  currency: string;
  completedAt: string;
}
